/**
 * The simulation.
 *
 * Pure and deterministic: same seed + same input log => same final score, on
 * any machine. Nothing in here touches the DOM, the clock, or Math.random, and
 * it is imported unchanged by the leaderboard worker so submitted runs can be
 * re-simulated before they're trusted.
 *
 * ── The idea ─────────────────────────────────────────────────────────────
 * You are a serpent of light. Your light burns down every tick, faster the
 * longer you get. Motes feed you. But length is also your score multiplier, so
 * the game pulls you two ways at once: grow to score, and starve for it.
 *
 * The release valve is the bite. Running into your own body does not kill you
 * — it severs the tail there, scatters the lost segments across the board as
 * embers, and floods you with their light. You trade your multiplier for
 * breathing room, then race to harvest what you cut off. Your fangs then need
 * time to regrow; touch yourself before they do and it is an ordinary death.
 */

import { Rng } from './rng'
import type {
  Bloom,
  DeathCause,
  Dir,
  Pickup,
  Rules,
  RunSummary,
  SimState,
  Vec,
} from './types'
import { DIR_VECTORS, opposite } from './types'

export function createSim(rules: Rules, seed: number): SimState {
  const mid = Math.floor(rules.grid / 2)
  const snake: Vec[] = []
  for (let i = 0; i < rules.startLength; i++) snake.push({ x: mid, y: mid + i })

  const s: SimState = {
    rulesId: rules.id,
    seed,
    tick: 0,
    grid: rules.grid,
    snake,
    dir: 0,
    buffer: [],
    grow: 0,
    light: rules.lightStart,
    score: 0,
    chain: 0,
    chainTimer: 0,
    motesEaten: 0,
    embersEaten: 0,
    bites: 0,
    longest: rules.startLength,
    coil: 1,
    pickups: [],
    blooms: [],
    biteCooldown: 0,
    recoil: 0,
    phase: 0,
    nextPrism: 0,
    alive: true,
    cause: 'none',
    events: [],
  }

  // One RNG per run, hung off the state so callers never have to thread it.
  rngs.set(s, new Rng(seed))
  const rng = rngOf(s)
  s.nextPrism = Math.floor(rng.range(rules.prismEvery[0], rules.prismEvery[1]))

  for (let i = 0; i < rules.maxMotes; i++) spawnMote(s)
  return s
}

// The RNG is deliberately not a field on SimState: SimState is plain data that
// gets structured-cloned into workers and JSON-logged in dev, and a class
// instance would not survive either.
const rngs = new WeakMap<SimState, Rng>()

function rngOf(s: SimState): Rng {
  let r = rngs.get(s)
  if (!r) {
    r = new Rng(s.seed)
    rngs.set(s, r)
  }
  return r
}

/**
 * Queue a turn. Returns true if it was accepted, which is the signal to append
 * it to the replay log — rejected inputs must not be recorded or the replay
 * and the live run would diverge.
 */
export function queueTurn(s: SimState, dir: Dir): boolean {
  if (!s.alive) return false
  if (s.buffer.length >= 2) return false
  const last = s.buffer.length ? s.buffer[s.buffer.length - 1] : s.dir
  if (dir === last || dir === opposite(last)) return false
  s.buffer.push(dir)
  return true
}

export function step(s: SimState, rules: Rules): void {
  s.events.length = 0
  if (!s.alive) return

  s.tick++

  // ── input ──────────────────────────────────────────────────────────────
  while (s.buffer.length) {
    const d = s.buffer.shift() as Dir
    if (d !== s.dir && d !== opposite(s.dir)) {
      s.dir = d
      break
    }
  }

  // ── move ───────────────────────────────────────────────────────────────
  const v = DIR_VECTORS[s.dir]
  const head = s.snake[0]
  let hx = head.x + v.x
  let hy = head.y + v.y

  const phased = s.phase > 0
  if (hx < 0 || hy < 0 || hx >= s.grid || hy >= s.grid) {
    if (phased) {
      hx = (hx + s.grid) % s.grid
      hy = (hy + s.grid) % s.grid
    } else {
      return die(s, 'wall', head.x, head.y)
    }
  }

  // ── self contact ───────────────────────────────────────────────────────
  // The tail tip vacates this tick unless we're growing into it, so touching
  // it is legal — the classic rule, and it keeps tight turns fair.
  const ignoreTail = s.grow === 0
  let hit = -1
  for (let i = 0; i < s.snake.length; i++) {
    if (ignoreTail && i === s.snake.length - 1) continue
    if (s.snake[i].x === hx && s.snake[i].y === hy) {
      hit = i
      break
    }
  }

  if (hit >= 0 && !phased) {
    const canBite =
      rules.biteEnabled && s.biteCooldown === 0 && s.snake.length >= rules.biteMinLength
    if (!canBite) return die(s, 'self', hx, hy)
    bite(s, rules, hit, hx, hy)
  }

  s.snake.unshift({ x: hx, y: hy })
  if (s.grow > 0) s.grow--
  else s.snake.pop()
  if (s.snake.length > s.longest) s.longest = s.snake.length

  // ── blooms ─────────────────────────────────────────────────────────────
  if (!phased) {
    for (const b of s.blooms) {
      if (b.x === hx && b.y === hy && bloomOpen(b, s.tick)) {
        return die(s, 'bloom', hx, hy)
      }
    }
  }

  // ── pickups ────────────────────────────────────────────────────────────
  for (let i = s.pickups.length - 1; i >= 0; i--) {
    const p = s.pickups[i]
    if (p.x !== hx || p.y !== hy) continue
    s.pickups.splice(i, 1)
    consume(s, rules, p)
  }

  // ── upkeep ─────────────────────────────────────────────────────────────
  for (let i = s.pickups.length - 1; i >= 0; i--) {
    const p = s.pickups[i]
    if (p.ttl !== Infinity && --p.ttl <= 0) s.pickups.splice(i, 1)
  }

  if (rules.maxMotes > 0) {
    let motes = 0
    for (const p of s.pickups) if (p.kind === 'mote') motes++
    while (motes < rules.maxMotes) {
      if (!spawnMote(s)) break
      motes++
    }
  }

  if (s.tick >= s.nextPrism && rules.prismEvery[0] < 1e8) {
    spawnPrism(s, rules)
    const rng = rngOf(s)
    s.nextPrism = s.tick + Math.floor(rng.range(rules.prismEvery[0], rules.prismEvery[1]))
  }

  if (s.chainTimer > 0 && --s.chainTimer === 0 && s.chain > 0) {
    s.events.push({ type: 'chainBreak', at: s.chain })
    s.chain = 0
  }

  if (s.biteCooldown > 0 && --s.biteCooldown === 0) s.events.push({ type: 'fangs' })
  if (s.recoil > 0) s.recoil--
  if (s.phase > 0 && --s.phase === 0) s.events.push({ type: 'phase', on: false })

  if (rules.lightEnabled && s.phase === 0) {
    const before = s.light
    const extra = Math.max(0, s.snake.length - rules.startLength)
    const drain =
      (rules.lightDrainBase + rules.lightDrainPerSegment * extra) *
      (1 + (s.coil - 1) * rules.lightDrainPerCoil)
    s.light -= drain
    if (before >= 25 && s.light < 25) s.events.push({ type: 'lowLight' })
    if (s.light <= 0) {
      s.light = 0
      return die(s, 'dark', s.snake[0].x, s.snake[0].y)
    }
  }
}

// ── mechanics ────────────────────────────────────────────────────────────

function bite(s: SimState, rules: Rules, at: number, hx: number, hy: number): void {
  const severed = s.snake.splice(at)
  s.bites++
  s.biteCooldown = rules.biteCooldown
  s.recoil = rules.recoilTicks

  // Everything you cut off comes back as light on the floor. Bite deep and the
  // board turns gold — but you have to go get it before it burns out.
  for (const seg of severed) {
    if (seg.x === hx && seg.y === hy) continue
    s.pickups.push({ kind: 'ember', x: seg.x, y: seg.y, ttl: rules.emberTtl, born: s.tick })
  }

  s.light = Math.min(rules.lightMax, s.light + rules.biteLightPerSegment * severed.length)
  const points = Math.round(5 * severed.length * lengthBonus(rules, s.snake.length + severed.length))
  s.score += points
  s.events.push({ type: 'bite', x: hx, y: hy, severed: severed.length, points })
}

function consume(s: SimState, rules: Rules, p: Pickup): void {
  const coilMult = 1 + (s.coil - 1) * 0.15
  let points = 0

  if (p.kind === 'mote') {
    s.grow += 1
    s.motesEaten++
    if (rules.chainMax > 0) {
      s.chain = Math.min(rules.chainMax, s.chain + 1)
      s.chainTimer = rules.chainWindow
    }
    s.light = Math.min(rules.lightMax, s.light + rules.moteLight)
    points = Math.round(10 * lengthBonus(rules, s.snake.length + s.grow) * chainMult(s) * coilMult)

    if (s.motesEaten % rules.motesPerCoil === 0) {
      s.coil++
      s.events.push({ type: 'coil', coil: s.coil })
      if (rules.bloomsEnabled) {
        for (let i = 0; i < rules.bloomsPerCoil && s.blooms.length < rules.bloomCap; i++) {
          spawnBloom(s)
        }
      }
    }
  } else if (p.kind === 'ember') {
    s.embersEaten++
    s.light = Math.min(rules.lightMax, s.light + rules.emberLight)
    // Embers don't build the chain, but they hold it open — so a deep bite
    // followed by a clean harvest keeps a hot streak alive.
    if (s.chain > 0) s.chainTimer = rules.chainWindow
    points = Math.round(8 * chainMult(s) * coilMult)
  } else {
    s.light = Math.min(rules.lightMax, s.light + rules.prismLight)
    s.phase = rules.phaseTicks
    s.events.push({ type: 'phase', on: true })
    points = Math.round(60 * coilMult)
  }

  s.score += points
  s.events.push({ type: 'eat', kind: p.kind, x: p.x, y: p.y, points, chain: s.chain })
}

function die(s: SimState, cause: DeathCause, x: number, y: number): void {
  s.alive = false
  s.cause = cause
  s.events.push({ type: 'death', cause, x, y })
}

function lengthBonus(rules: Rules, len: number): number {
  return 1 + Math.max(0, len - rules.startLength) * 0.04
}

function chainMult(s: SimState): number {
  return 1 + s.chain * 0.25
}

export function bloomOpen(b: Bloom, tick: number): boolean {
  return (tick + b.phase) % b.period < b.openFor
}

/** 0..1 through the bloom's cycle — renderers use it to animate the petals. */
export function bloomCycle(b: Bloom, tick: number): number {
  return ((tick + b.phase) % b.period) / b.period
}

// ── spawning ─────────────────────────────────────────────────────────────

function occupancy(s: SimState): Uint8Array {
  const g = s.grid
  const occ = new Uint8Array(g * g)
  for (const seg of s.snake) occ[seg.y * g + seg.x] = 1
  for (const p of s.pickups) occ[p.y * g + p.x] = 1
  for (const b of s.blooms) occ[b.y * g + b.x] = 1
  return occ
}

/**
 * Pick a free cell. `minHeadDist` keeps hazards from materialising in the
 * player's face; motes pass 0 and can land anywhere legal.
 */
function freeCell(s: SimState, minHeadDist: number): Vec | null {
  const occ = occupancy(s)
  const head = s.snake[0]
  const g = s.grid
  const candidates: number[] = []
  for (let i = 0; i < occ.length; i++) {
    if (occ[i]) continue
    const x = i % g
    const y = (i / g) | 0
    if (minHeadDist > 0 && Math.abs(x - head.x) + Math.abs(y - head.y) < minHeadDist) continue
    candidates.push(i)
  }
  if (!candidates.length) return null
  const idx = candidates[rngOf(s).int(candidates.length)]
  return { x: idx % g, y: (idx / g) | 0 }
}

function spawnMote(s: SimState): boolean {
  const cell = freeCell(s, 0)
  if (!cell) return false
  s.pickups.push({ kind: 'mote', x: cell.x, y: cell.y, ttl: Infinity, born: s.tick })
  return true
}

function spawnPrism(s: SimState, rules: Rules): void {
  const cell = freeCell(s, 3)
  if (!cell) return
  s.pickups.push({ kind: 'prism', x: cell.x, y: cell.y, ttl: rules.prismTtl, born: s.tick })
}

function spawnBloom(s: SimState): void {
  const cell = freeCell(s, 5)
  if (!cell) return
  const rng = rngOf(s)
  const period = 60 + rng.int(50)
  s.blooms.push({
    x: cell.x,
    y: cell.y,
    period,
    openFor: Math.floor(period * rng.range(0.45, 0.62)),
    phase: rng.int(period),
    born: s.tick,
  })
}

// ── output ───────────────────────────────────────────────────────────────

export function summarize(s: SimState): RunSummary {
  return {
    mode: s.rulesId,
    seed: s.seed,
    score: s.score,
    ticks: s.tick,
    motes: s.motesEaten,
    embers: s.embersEaten,
    bites: s.bites,
    longest: s.longest,
    coil: s.coil,
    cause: s.cause,
  }
}
